import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApiError,
  UnauthorizedError,
  documentsApi,
  type CatalogDocumentDTO,
} from '@/shared/api'
import { useAuth } from '@/features/auth'
import { useScope } from '@/features/scope'
import { useAppState } from '@/app/providers/AppStateProvider'
import { useConversations } from '@/features/conversations/ConversationsProvider'
import { Icon } from '@/shared/ui/Icon'
import { BetaUploadNotice } from '@/shared/ui/BetaUploadNotice'
import { DocumentMetadataForm } from '@/features/documents/DocumentMetadataForm'
import {
  ACCEPTED_FILE_LABEL,
  DEFAULT_UPLOAD_METADATA,
  deriveDocumentIdFromFilename,
  documentTitle,
  findDuplicateDocument,
  formatBytes,
} from '@/features/documents/documentUtils'
import type {
  UploadFormErrors,
  UploadFormValues,
} from '@/features/documents/types'
import type { GuidedIngestionStatus } from './types'

interface Props {
  file: File
  onClose: () => void
}

function buildInitialValues(file: File): UploadFormValues {
  return {
    file,
    // Defaults intentionally do NOT pre-fill entity_id — the spec calls
    // that out explicitly so we never quietly stamp the wrong patient on
    // a freshly attached document.
    entityType: DEFAULT_UPLOAD_METADATA.entityType,
    entityId: '',
    groupType: DEFAULT_UPLOAD_METADATA.groupType,
    groupId: '',
    documentId: deriveDocumentIdFromFilename(file.name),
    // Step 9.5 — optional human display names. Empty by default; the
    // guided flow currently does not expose them but the field must exist
    // so the shared upload form type stays satisfied.
    entityDisplayName: '',
    groupDisplayName: '',
    title: '',
  }
}

function validate(values: UploadFormValues): UploadFormErrors {
  const errors: UploadFormErrors = {}
  if (!values.file) errors.file = 'Selecione um arquivo.'
  if (!values.entityType.trim()) errors.entityType = 'Obrigatório.'
  if (!values.entityId.trim()) errors.entityId = 'Obrigatório.'
  if (!values.documentId.trim()) errors.documentId = 'Obrigatório.'

  const groupTypeFilled = values.groupType.trim().length > 0
  const groupIdFilled = values.groupId.trim().length > 0
  if (groupTypeFilled !== groupIdFilled) {
    const message = 'Preencha tipo e ID do grupo juntos.'
    errors.groupType = message
    errors.groupId = message
  }

  return errors
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * STEP 7.10 — Guided ingestion modal.
 *
 * Hosted by {@link GuidedIngestionProvider}. Built directly on top of the
 * Documents Library upload primitives so the experience stays consistent
 * across the two surfaces and there is exactly one place where ingest
 * happens:
 *
 *   - {@link DocumentMetadataForm} renders the fields and the
 *     "Metadados avançados" disclosure exactly like the Library panel does;
 *   - {@link isDuplicateDocumentId} reuses the same client-side duplicate
 *     check (tenant + entity + group + document_id);
 *   - {@link documentsApi.uploadDocument} is the single ingest entry point;
 *   - {@link useScope.reload} returns the fresh catalog snapshot so we can
 *     verify the just-uploaded row exists before flipping the active scope.
 *
 * Success flow:
 *   1. POST /ingest with the tenant from auth (never typed by the user).
 *   2. Reload the catalog cached in ScopeProvider and locate the new row
 *      by the full coordinate tuple (entity/group/document_id).
 *   3. selectDocument(found) — turns the uploaded file into the active
 *      retrieval scope.
 *   4. startNewConsultation() + setWorkspace('chat') — the next prompt
 *      creates a brand new conversation with the scope locked in, even if
 *      the user opened the dialog from an existing thread. This mirrors the
 *      Library "Usar no chat" behavior so attaching a document never leaks
 *      retrieval context into an unrelated conversation.
 *   5. Show a brief success state, then close.
 *
 * Failure flow:
 *   - Backend rejection / network error → readable message + retry/cancel.
 *   - Reload succeeds but new row is missing → explicit warning, scope is
 *     NOT changed, user is invited to open the Library manually.
 */
export function GuidedIngestionDialog({ file, onClose }: Props) {
  const { organization, logout } = useAuth()
  const {
    documents: catalogSnapshot,
    selectDocument,
    reload: reloadScope,
  } = useScope()
  const { setWorkspace } = useAppState()
  const { startNewConsultation } = useConversations()

  const tenantId = organization?.tenant_id ?? ''
  const orgLabel = organization?.display_name ?? organization?.slug ?? '—'

  const [values, setValues] = useState<UploadFormValues>(() => buildInitialValues(file))
  const [errors, setErrors] = useState<UploadFormErrors>({})
  const [status, setStatus] = useState<GuidedIngestionStatus>('idle')
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [warning, setWarning] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = 'guided-ingestion-title'

  // If a different file is handed in (provider opens again with another
  // pick), reset the form to defaults so the user never sees stale state
  // from a previous attempt.
  useEffect(() => {
    setValues(buildInitialValues(file))
    setErrors({})
    setConfirmReplace(false)
    setWarning(null)
    setStatus('idle')
  }, [file])

  // Focus the dialog on mount so keyboard users land inside it. We focus the
  // container itself rather than the first input — the metadata defaults are
  // already filled, and starting on the dialog lets Escape work immediately.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const busy = status === 'submitting' || status === 'selecting'

  // Escape closes the dialog unless an upload is in flight. Backdrop click
  // follows the same rule. We never want to drop the modal mid-request and
  // leave a half-applied scope behind.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [busy, onClose])

  const updateField = useCallback(
    (field: keyof UploadFormValues, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }))
      setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }))
      if (field === 'documentId') {
        setConfirmReplace(false)
      }
      if (
        field === 'entityType' ||
        field === 'entityId' ||
        field === 'groupType' ||
        field === 'groupId'
      ) {
        setConfirmReplace(false)
      }
    },
    [],
  )

  const effectiveDocumentId = useMemo(() => {
    const typed = values.documentId.trim()
    if (typed) return typed
    return deriveDocumentIdFromFilename(file.name)
  }, [values.documentId, file])

  // Step 13.7-FIX — duplicate keyed on tenant + document_id (the key the
  // backend uses for selection/delete/retrieval). Returns the matched row so
  // the warning can name it.
  const duplicateDocument = useMemo(
    () => findDuplicateDocument(catalogSnapshot, tenantId, effectiveDocumentId),
    [catalogSnapshot, tenantId, effectiveDocumentId],
  )
  const isDuplicate = duplicateDocument !== null

  useEffect(() => {
    if (!isDuplicate && confirmReplace) setConfirmReplace(false)
  }, [isDuplicate, confirmReplace])

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault()
      if (busy || !tenantId) return

      const validation = validate(values)
      if (Object.keys(validation).length > 0) {
        setErrors(validation)
        return
      }

      if (isDuplicate && !confirmReplace) {
        setErrors({
          form:
            'Já existe um documento com esse ID neste escopo. Marque ' +
            '"Substituir documento existente" para reindexar ou edite o ID.',
        })
        return
      }

      const submittedFile = values.file ?? file
      const documentId =
        values.documentId.trim() || deriveDocumentIdFromFilename(submittedFile.name)
      const entityType = values.entityType.trim()
      const entityId = values.entityId.trim()
      const groupType = values.groupType.trim() || null
      const groupId = values.groupId.trim() || null

      setErrors({})
      setWarning(null)
      setStatus('submitting')

      try {
        const response = await documentsApi.uploadDocument({
          file: submittedFile,
          tenantId,
          entityType,
          entityId,
          groupType,
          groupId,
          documentId,
          entityDisplayName: values.entityDisplayName.trim() || null,
          groupDisplayName: values.groupDisplayName.trim() || null,
          title: values.title.trim() || null,
        })

        // Reload the catalog cached in ScopeProvider and find the row that
        // matches the upload response by the *full* coordinate tuple — a
        // bare document_id match could otherwise grab a sibling row that
        // happens to share the same id in a different entity/group.
        setStatus('selecting')
        const freshDocuments = await reloadScope()
        const found = freshDocuments.find(
          (d) =>
            d.tenant_id === response.tenant_id &&
            d.document_id === response.document_id &&
            d.entity_type === response.entity_type &&
            d.entity_id === response.entity_id &&
            (d.group_type ?? null) === (response.group_type ?? null) &&
            (d.group_id ?? null) === (response.group_id ?? null),
        )

        if (!found) {
          // Upload succeeded but the catalog couldn't be re-read or the row
          // hasn't shown up yet. Don't flip scope to a synthetic record —
          // the user is told explicitly and can finish manually via the
          // Biblioteca. The scope provider's state already reflects whatever
          // catalog we did get back.
          setStatus('success')
          setWarning(
            'Documento enviado, mas não foi possível selecioná-lo automaticamente. ' +
              'Atualize a Biblioteca ou selecione manualmente.',
          )
          return
        }

        selectDocument(found)
        // Mirror "Usar no chat" behaviour from the Library so uploading a
        // document from inside an existing thread always lands the user on
        // a fresh New Consultation prompt with the new scope locked in.
        startNewConsultation()
        setWorkspace('chat')

        setStatus('success')

        // Brief confirmation pause so the user sees what happened, then
        // close the dialog and return to the composer.
        window.setTimeout(() => {
          onClose()
        }, 900)
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await logout()
          return
        }
        setStatus('error')
        setErrors({
          form: errorDetail(err, 'Falha ao enviar documento.'),
        })
      }
    },
    [
      busy,
      tenantId,
      values,
      file,
      isDuplicate,
      confirmReplace,
      reloadScope,
      selectDocument,
      startNewConsultation,
      setWorkspace,
      onClose,
      logout,
    ],
  )

  const handleBackdropClick = useCallback(() => {
    if (busy) return
    onClose()
  }, [busy, onClose])

  // Block Enter-to-submit inside text fields so the upload always requires
  // an explicit click on the primary CTA. The submit button itself still
  // reacts to keyboard activation (Space/Enter when focused).
  const handleFormKeyDown = useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter') return
    const target = event.target as HTMLElement
    if (
      target instanceof HTMLInputElement &&
      target.type !== 'submit' &&
      target.type !== 'checkbox' &&
      target.type !== 'file'
    ) {
      event.preventDefault()
    }
  }, [])

  const submitLabel =
    status === 'submitting'
      ? 'Enviando e indexando…'
      : status === 'selecting'
        ? 'Selecionando escopo…'
        : status === 'success'
          ? 'Pronto'
          : isDuplicate
            ? 'Substituir e usar no chat'
            : 'Enviar e usar no chat'

  return (
    <div
      className="confirm-overlay guided-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={busy || undefined}
      data-testid="guided-ingestion-dialog"
      onClick={handleBackdropClick}
    >
      <div
        className="guided-modal"
        onClick={(e) => e.stopPropagation()}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="guided-modal-head">
          <h3 id={titleId}>Adicionar documento ao chat</h3>
          <p>Envie um arquivo, organize o escopo e use-o como contexto da próxima consulta.</p>
        </header>

        <BetaUploadNotice compact />

        <form
          className="guided-modal-form doc-upload-form"
          onSubmit={(e) => void handleSubmit(e)}
          onKeyDown={handleFormKeyDown}
          noValidate
        >
          <div
            className={`guided-file${status === 'submitting' ? ' is-uploading' : ''}`}
            aria-live="polite"
          >
            <div className="guided-file-icon" aria-hidden="true">
              <Icon name="paperclip" />
            </div>
            <div className="guided-file-meta">
              <div className="guided-file-name" title={file.name}>{file.name}</div>
              <div className="guided-file-bytes">
                {formatBytes(file.size)} · {ACCEPTED_FILE_LABEL}
              </div>
            </div>
            <div className="guided-file-org" title={`tenant ${tenantId}`}>
              <Icon name="grid" />
              <span>{orgLabel}</span>
            </div>
            {status === 'submitting' ? (
              <div className="guided-file-progress" aria-hidden="true">
                <span />
              </div>
            ) : null}
          </div>

          <DocumentMetadataForm
            values={values}
            errors={errors}
            disabled={busy}
            onChange={updateField}
          />

          {duplicateDocument ? (
            <div className="doc-upload-alert is-warning" role="alert">
              <Icon name="sparkles" />
              <div>
                <strong>Este document_id já existe neste workspace.</strong>
                <p>
                  O ID <code>{effectiveDocumentId}</code> corresponde a{' '}
                  <strong>{documentTitle(duplicateDocument)}</strong>
                  {duplicateDocument.filename &&
                  documentTitle(duplicateDocument) !== duplicateDocument.filename
                    ? ` (${duplicateDocument.filename})`
                    : ''}
                  . Se continuar com substituição ativada, o documento existente
                  será <strong>reindexado</strong>. Altere o document_id para criar
                  um novo documento.
                </p>
                <label className="doc-upload-confirm">
                  <input
                    type="checkbox"
                    checked={confirmReplace}
                    onChange={(e) => setConfirmReplace(e.target.checked)}
                    disabled={busy}
                  />
                  <span>Substituir documento existente</span>
                </label>
              </div>
            </div>
          ) : null}

          {errors.form ? (
            <div className="doc-upload-alert is-error" role="alert">
              <Icon name="close" /> {errors.form}
            </div>
          ) : null}

          {warning ? (
            <div className="doc-upload-alert is-warning" role="alert">
              <Icon name="sparkles" />
              <div>
                <strong>Documento enviado.</strong>
                <p>{warning}</p>
              </div>
            </div>
          ) : null}

          {status === 'success' && !warning ? (
            <div className="doc-upload-alert is-success" role="status">
              <Icon name="sparkles" />
              <span>Documento indexado e selecionado como escopo.</span>
            </div>
          ) : null}

          <footer className="guided-modal-actions">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`doc-upload-submit${isDuplicate ? ' is-replace' : ''}`}
              disabled={busy || status === 'success'}
            >
              {submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

// Re-export so callers can build synthetic-DTO helpers if they ever need
// to bypass the catalog round-trip. Kept here intentionally to avoid a
// circular dependency between scope and ingestion.
export type GuidedIngestionDocument = CatalogDocumentDTO
